// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef } from 'react';
import { AUTO_SAVE_DEBOUNCE_MS } from '../constants';

type AutoSaveOptions = {
  // Optional fields (a round title) must be clearable, so an empty value is a
  // legitimate save. Required fields (the tour name) keep the non-blank guard.
  allowEmpty?: boolean;
};

export function useAutoSave(
  value: string,
  lastSavedValue: string,
  onSave: (value: string) => void,
  options: AutoSaveOptions = {}
) {
  const { allowEmpty = false } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveRef = useRef(onSave);
  const lastSavedRef = useRef(lastSavedValue);
  // Holds the value waiting on the debounce so unmount can still flush it.
  const pendingRef = useRef<string | null>(null);

  onSaveRef.current = onSave;
  lastSavedRef.current = lastSavedValue;

  useEffect(() => {
    const trimmed = value.trim();
    const unchanged = trimmed === lastSavedRef.current.trim();
    const blocked = !trimmed && !allowEmpty;

    if (unchanged || blocked) {
      // Nothing to save. Drop anything queued by earlier keystrokes so the
      // unmount flush below cannot resurrect a value the user has undone.
      pendingRef.current = null;
      return;
    }

    pendingRef.current = trimmed;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const queued = pendingRef.current;
      pendingRef.current = null;
      if (queued !== null) onSaveRef.current(queued);
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, allowEmpty]);

  // Flush on unmount. Without this, switching editor step or navigating away
  // inside the debounce window silently discards the last edit — the cleanup
  // above only cancels the timer. Runs after that cleanup, which is why the
  // guard is on pendingRef rather than timerRef.
  useEffect(
    () => () => {
      const queued = pendingRef.current;
      pendingRef.current = null;
      if (queued !== null) onSaveRef.current(queued);
    },
    []
  );
}
